import { z } from 'zod';

import { entityIdSchema, isoDateSchema, isoDateTimeSchema, pageMetaSchema } from './common.js';

export const riskStatusSchema = z.enum(['none', 'red_list', 'blacklist']);
export const customerRiskTypeSchema = z.enum(['red_list', 'blacklist']);
export const customerEmojiSchema = z.enum([
  '🏷️',
  '⭐',
  '💚',
  '🌿',
  '🎯',
  '⚠️',
  '⛔',
  '💼',
  '👪',
  '♿',
]);
export const customerLabelActionSchema = z.enum(['warning', 'block_reservation']);

export const customerLabelDefinitionSchema = z.object({
  id: entityIdSchema,
  title: z.string().trim().min(1).max(80),
  color: z.string().regex(/^#[0-9a-f]{6}$/iu),
  emoji: customerEmojiSchema,
  businessAction: customerLabelActionSchema.nullable(),
  systemKey: customerRiskTypeSchema.nullable(),
  opensComment: z.boolean(),
  showCommentOnHover: z.boolean(),
  active: z.boolean(),
  displayOrder: z.number().int(),
  revision: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});

export const customerLabelAssignmentSchema = z.object({
  id: entityIdSchema,
  customerId: entityIdSchema,
  labelId: entityIdSchema,
  comment: z.string().max(2_000),
  revision: z.number().int().positive(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  label: customerLabelDefinitionSchema,
});

export const customerLabelSelectionSchema = z.object({
  labelId: entityIdSchema,
  comment: z.string().trim().max(2_000).default(''),
});

export const customerRiskFlagSchema = z.object({
  type: customerRiskTypeSchema,
  reason: z.string().max(2_000),
  active: z.boolean(),
  createdAt: isoDateTimeSchema,
});

const structuredCustomerFields = {
  firstName: z.string().trim().max(120).default(''),
  lastName: z.string().trim().max(120).default(''),
  company: z.string().trim().max(180).default(''),
  email: z.union([z.email(), z.literal('')]).default(''),
  phone: z.string().trim().max(40).default(''),
  country: z.string().trim().max(80).default('GF'),
  phoneCountryCode: z.string().trim().max(8).default('+594'),
  addressLine1: z.string().trim().max(240).default(''),
  addressLine2: z.string().trim().max(240).default(''),
  postalCode: z.string().trim().max(24).default(''),
  city: z.string().trim().max(120).default(''),
};

export const customerInputSchema = z
  .object({
    ...structuredCustomerFields,
    labels: z.array(z.string().trim().min(1).max(80)).max(30).default([]),
    labelAssignments: z.array(customerLabelSelectionSchema).max(30).default([]),
    allergies: z.array(z.string().trim().min(1).max(160)).max(50).default([]),
    diets: z.array(z.string().trim().min(1).max(160)).max(50).default([]),
    notes: z.string().trim().max(10_000).default(''),
    riskStatus: riskStatusSchema.default('none'),
    riskReason: z.string().trim().max(2_000).default(''),
    redList: z.boolean().default(false),
    redListReason: z.string().trim().max(2_000).default(''),
    blacklist: z.boolean().default(false),
    blacklistReason: z.string().trim().max(2_000).default(''),
    confirmDuplicateCandidates: z.boolean().default(false),
  })
  .superRefine((customer, context) => {
    if (customer.company !== '' || (customer.firstName !== '' && customer.lastName !== '')) return;
    context.addIssue({
      code: 'custom',
      message: 'Un prénom et un nom, ou une société, sont obligatoires.',
      path: ['lastName'],
    });
  });

export const customerSummaryItemSchema = z.object({
  id: entityIdSchema,
  reference: z.string().min(1),
  firstName: z.string(),
  lastName: z.string(),
  company: z.string(),
  email: z.string(),
  phone: z.string(),
  labels: z.array(customerLabelAssignmentSchema),
  risks: z.array(customerRiskFlagSchema),
  riskStatus: riskStatusSchema,
  archived: z.boolean(),
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  revision: z.number().int().positive(),
});

export const customerDetailSchema = customerSummaryItemSchema.extend({
  country: z.string(),
  phoneCountryCode: z.string(),
  addressLine1: z.string(),
  addressLine2: z.string(),
  postalCode: z.string(),
  city: z.string(),
  notes: z.string(),
  allergies: z.array(z.string()),
  diets: z.array(z.string()),
  archivedAt: isoDateTimeSchema.nullable(),
  archivedReason: z.string(),
  riskReason: z.string(),
});

// Alias de compatibilité des services déjà présents : le runtime renvoie désormais le détail canonique.
export const customerSchema = customerDetailSchema;

export const customerUpdateSchema = z.object({
  firstName: z.string().trim().max(120).optional(),
  lastName: z.string().trim().max(120).optional(),
  company: z.string().trim().max(180).optional(),
  email: z.union([z.email(), z.literal('')]).optional(),
  phone: z.string().trim().max(40).optional(),
  country: z.string().trim().max(80).optional(),
  phoneCountryCode: z.string().trim().max(8).optional(),
  addressLine1: z.string().trim().max(240).optional(),
  addressLine2: z.string().trim().max(240).optional(),
  postalCode: z.string().trim().max(24).optional(),
  city: z.string().trim().max(120).optional(),
  labels: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
  labelAssignments: z.array(customerLabelSelectionSchema).max(30).optional(),
  allergies: z.array(z.string().trim().min(1).max(160)).max(50).optional(),
  diets: z.array(z.string().trim().min(1).max(160)).max(50).optional(),
  notes: z.string().trim().max(10_000).optional(),
  riskStatus: riskStatusSchema.optional(),
  riskReason: z.string().trim().max(2_000).optional(),
  redList: z.boolean().optional(),
  redListReason: z.string().trim().max(2_000).optional(),
  blacklist: z.boolean().optional(),
  blacklistReason: z.string().trim().max(2_000).optional(),
  confirmDuplicateCandidates: z.boolean().optional(),
  revision: z.number().int().positive(),
});

export const customerListQuerySchema = z.object({
  cursor: z.string().trim().min(1).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  search: z.string().trim().max(200).optional(),
  labelId: entityIdSchema.optional(),
  includeArchived: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .transform((value) => value === true || value === 'true')
    .default(false),
});

export const customerPageSchema = z.object({
  items: z.array(customerSummaryItemSchema),
  page: pageMetaSchema,
});

export const customerArchiveInputSchema = z.object({
  revision: z.number().int().positive(),
  reason: z.string().trim().min(1).max(2_000),
});
export const customerArchiveResultSchema = z.object({ archived: z.literal(true) });

export const customerArchiveQuerySchema = z.object({
  revision: z.coerce.number().int().positive(),
  reason: z.string().trim().min(1).max(2_000).optional(),
});

export const customerDuplicateCandidateSchema = z.object({
  customerId: entityIdSchema,
  reference: z.string(),
  displayName: z.string(),
  email: z.string(),
  phone: z.string(),
  match: z.enum(['email', 'phone', 'name']),
  strength: z.enum(['blocking', 'strong', 'candidate']),
});

export const customerDuplicateCheckSchema = z.object({
  candidates: z.array(customerDuplicateCandidateSchema),
  canCreate: z.boolean(),
});

const nullableCount = z.number().int().nonnegative().nullable();
const nullableMoney = z.number().int().nonnegative().nullable();

export const customerSummarySchema = z.object({
  reservationCount: nullableCount,
  nextReservation: z
    .object({
      date: isoDateSchema,
      type: z.string(),
      participantCount: z.number().int().nonnegative(),
    })
    .nullable(),
  creditCount: nullableCount,
  purchasedGiftVoucherCount: nullableCount,
  pendingQuoteCount: nullableCount,
  outstandingCents: nullableMoney,
  reservationTotalCents: nullableMoney,
});

export const customerReservationSchema = z.object({
  id: entityIdSchema,
  reference: z.string(),
  date: isoDateSchema,
  type: z.string(),
  participantCount: z.number().int().nonnegative(),
  status: z.string(),
  amountCents: z.number().int().nonnegative(),
  outstandingCents: z.number().int(),
});

export const customerPaymentSchema = z.object({
  id: entityIdSchema,
  date: isoDateSchema.nullable(),
  amountCents: z.number().int(),
  method: z.string(),
  linkedLabel: z.string(),
  status: z.string(),
  reference: z.string(),
});

export const customerQuoteSchema = z.object({
  id: entityIdSchema,
  reference: z.string(),
  date: isoDateSchema,
  type: z.string(),
  status: z.string(),
  amountCents: z.number().int().nonnegative(),
  validUntil: isoDateSchema.nullable(),
});

export const customerCreditSchema = z.object({
  id: entityIdSchema,
  code: z.string(),
  initialCents: z.number().int().positive(),
  remainingCents: z.number().int().nonnegative(),
  status: z.string(),
  expiresOn: isoDateSchema.nullable(),
  sourceReference: z.string(),
});

export const customerGiftVoucherSchema = z.object({
  id: entityIdSchema,
  code: z.string(),
  purchaser: z.string(),
  beneficiary: z.string(),
  amountCents: z.number().int().nonnegative(),
  balanceCents: z.number().int().nonnegative(),
  status: z.string(),
  purchasedOn: isoDateSchema.nullable(),
  expiresOn: isoDateSchema.nullable(),
  usedReservationReference: z.string(),
});

export const customerHistoryEventSchema = z.object({
  id: entityIdSchema,
  occurredAt: isoDateTimeSchema,
  author: z.string(),
  action: z.string(),
  detail: z.string(),
  relatedEntity: z.string().nullable(),
});

function linkedPage<T extends z.ZodType>(item: T) {
  return z.object({ items: z.array(item), page: pageMetaSchema });
}

export const customerReservationPageSchema = linkedPage(customerReservationSchema);
export const customerPaymentPageSchema = linkedPage(customerPaymentSchema);
export const customerQuotePageSchema = linkedPage(customerQuoteSchema);
export const customerCreditPageSchema = linkedPage(customerCreditSchema);
export const customerGiftVoucherPageSchema = linkedPage(customerGiftVoucherSchema);
export const customerHistoryPageSchema = linkedPage(customerHistoryEventSchema);

export const customerLabelInputSchema = z.object({
  title: z.string().trim().min(1).max(80),
  color: z.string().regex(/^#[0-9a-f]{6}$/iu),
  emoji: customerEmojiSchema,
  businessAction: customerLabelActionSchema.nullable().default(null),
  opensComment: z.boolean().default(false),
  showCommentOnHover: z.boolean().default(false),
  active: z.boolean().default(true),
  displayOrder: z.number().int().min(-1_000).max(10_000).default(0),
});

export const customerLabelUpdateSchema = z.object({
  title: z.string().trim().min(1).max(80).optional(),
  color: z
    .string()
    .regex(/^#[0-9a-f]{6}$/iu)
    .optional(),
  emoji: customerEmojiSchema.optional(),
  businessAction: customerLabelActionSchema.nullable().optional(),
  opensComment: z.boolean().optional(),
  showCommentOnHover: z.boolean().optional(),
  active: z.boolean().optional(),
  displayOrder: z.number().int().min(-1_000).max(10_000).optional(),
  revision: z.number().int().positive(),
});

export const customerLabelArchiveInputSchema = z.object({ revision: z.number().int().positive() });
export const customerLabelListSchema = z.array(customerLabelDefinitionSchema);

export const customerImportInputSchema = z.object({
  sourceName: z.string().trim().min(1).max(180),
  encoding: z.enum(['utf-8', 'windows-1252']),
  content: z.string().min(1).max(2_000_000),
});

export const customerImportIssueSchema = z.object({
  row: z.number().int().positive(),
  level: z.enum(['error', 'warning']),
  code: z.string(),
  message: z.string(),
});

export const customerImportRowSchema = z.object({
  row: z.number().int().positive(),
  input: customerInputSchema,
  outcome: z.enum(['create', 'update', 'blocked']),
  existingCustomerId: entityIdSchema.nullable(),
});

export const customerImportPreviewSchema = z.object({
  mapping: z.record(z.string(), z.string()),
  totalRows: z.number().int().nonnegative(),
  validRows: z.number().int().nonnegative(),
  errorRows: z.number().int().nonnegative(),
  issues: z.array(customerImportIssueSchema),
  rows: z.array(customerImportRowSchema),
  requestHash: z.string().length(64),
});

export const customerImportApplySchema = customerImportInputSchema.extend({
  idempotencyKey: z.string().trim().min(8).max(160),
  requestHash: z.string().length(64),
});

export const customerImportResultSchema = z.object({
  importId: entityIdSchema,
  created: z.number().int().nonnegative(),
  updated: z.number().int().nonnegative(),
  blocked: z.number().int().nonnegative(),
  idempotentReplay: z.boolean(),
  issues: z.array(customerImportIssueSchema),
});

export type Customer = z.infer<typeof customerDetailSchema>;
export type CustomerSummaryItem = z.infer<typeof customerSummaryItemSchema>;
export type CustomerDetail = z.infer<typeof customerDetailSchema>;
export type CustomerInput = z.infer<typeof customerInputSchema>;
export type CustomerUpdate = z.infer<typeof customerUpdateSchema>;
export type CustomerRiskStatus = z.infer<typeof riskStatusSchema>;
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;
export type CustomerLabelDefinition = z.infer<typeof customerLabelDefinitionSchema>;
export type CustomerLabelAssignment = z.infer<typeof customerLabelAssignmentSchema>;
export type CustomerDuplicateCandidate = z.infer<typeof customerDuplicateCandidateSchema>;
export type CustomerDuplicateCheck = z.infer<typeof customerDuplicateCheckSchema>;
export type CustomerHistoryEvent = z.infer<typeof customerHistoryEventSchema>;
export type CustomerSummary = z.infer<typeof customerSummarySchema>;
export type CustomerImportRow = z.infer<typeof customerImportRowSchema>;
export type CustomerLabelInput = z.infer<typeof customerLabelInputSchema>;
export type CustomerImportApply = z.infer<typeof customerImportApplySchema>;
export type CustomerImportPreview = z.infer<typeof customerImportPreviewSchema>;
export type CustomerImportResult = z.infer<typeof customerImportResultSchema>;
